import { motion } from "framer-motion";
import { Crown, BedDouble, Users, ArrowRight, Sparkles } from "lucide-react";
import { HOTEL } from "../data/site";

const steps = [
  {
    icon: Crown,
    title: "Host the Wedding",
    body: "Book the Kalyana Mandap for the ceremony, baraat reception and pheras — with traditional Odia décor.",
  },
  {
    icon: Sparkles,
    title: "Continue the Reception",
    body: "The same property transitions seamlessly into a reception hall — premium seating, lighting and catering on-site.",
  },
  {
    icon: BedDouble,
    title: "Stay Comfortably",
    body: "Out-of-town guests and the bridal party rest in our AC, Deluxe and Family rooms steps away from the hall.",
  },
  {
    icon: Users,
    title: "All Under One Roof",
    body: "Less travel, fewer logistics, more time with family — that's the Maa Durga Palace promise.",
  },
];

export default function StayAndEvent() {
  return (
    <section
      id="stay-and-event"
      data-testid="stay-event-section"
      className="relative section-padding overflow-hidden bg-[#100707] text-white"
    >
      {/* Layered background */}
      <div aria-hidden className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(122,26,36,0.45),transparent_60%)]" />
      </div>
      <div aria-hidden className="absolute inset-0 grain opacity-100 pointer-events-none" />

      <div className="container-x relative">
        <div className="max-w-3xl">
          <span className="text-xs sm:text-sm font-medium tracking-[0.25em] uppercase text-[#E6C555]">
            One Venue · One Experience
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Host, celebrate and stay —<br />
            <span className="italic text-[#D4AF37]">all under one royal roof.</span>
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed text-base sm:text-lg">
            Maa Durga Palace is built on a simple idea: your wedding shouldn't be scattered
            across the city. Host the ceremony, the reception <em>and</em> accommodate every
            relative — without leaving the property.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                data-testid={`stay-event-step-${i}`}
                className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 hover:bg-white/[0.07] hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#D4AF37]/15 text-[#E6C555] grid place-items-center">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <span className="font-display text-2xl text-[#D4AF37]/90">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-display text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.body}</p>
                {i < steps.length - 1 && (
                  <ArrowRight
                    size={18}
                    className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/70"
                  />
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-3">
          <a
            href={HOTEL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stay-event-book-venue"
            className="btn-gold"
          >
            <Sparkles size={16} />
            Book the Venue
          </a>
          <a
            href="#stay"
            data-testid="stay-event-explore-stay"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-medium text-sm tracking-wide border border-white/30 text-white hover:bg-white/10 transition-all duration-300"
          >
            Explore Rooms
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
